'use client';

import { Button } from '@components/ui/button';
import {
  Sheet,
  SheetContent,
  SheetTitle,
  SheetTrigger
} from '@components/ui/sheet';
import { VisuallyHidden } from '@components/ui/visually-hidden';
import { Menu } from 'lucide-react';
import Link from 'next/link';
import { FC, useState } from 'react';
import LqdLogo from '../LqdLogo';
import SignUpCTA from '../SignUpCTA';

export const navbarLinks = [
  { href: '#features', name: 'Features' },
  { href: '#pricing', name: 'Pricing' },
  { href: '#faq', name: 'FAQ' },
  { href: '#contact', name: 'Contact' }
];

type NavbarProps = {
  renderNavLinks?: boolean;
  organizationId?: string | undefined;
  inviteOnlyLogin?: boolean;
};

const Navbar: FC<NavbarProps> = ({
  renderNavLinks = true,
  organizationId = undefined,
  inviteOnlyLogin = false
}) => {
  const [open, setOpen] = useState(false);
  const clerkButtons = <SignUpCTA inviteOnlyLogin={inviteOnlyLogin} />;

  return (
    <nav className="border-b bg-background dark:bg-black">
      <div className="mx-auto flex h-20 max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
        <div className="flex items-center">
          <Link href="/" className="flex items-center space-x-2">
            <LqdLogo />
          </Link>
        </div>

        <div className="hidden md:block">
          <div className="flex items-center space-x-8">
            {navbarLinks.map(item => (
              <Link
                key={item.name}
                href={item.href}
                className="text-sm font-medium text-muted-foreground transition-colors hover:text-foreground"
              >
                {item.name}
              </Link>
            ))}
          </div>
        </div>

        <div className="hidden md:flex md:items-center md:space-x-3">
          {clerkButtons}
        </div>

        {/* Mobile menu button */}
        <div className="md:hidden">
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon">
                <Menu className="h-6 w-6" />
                <span className="sr-only">Open menu</span>
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[300px] sm:w-[400px]">
              <VisuallyHidden>
                <SheetTitle>Menu</SheetTitle>
              </VisuallyHidden>
              <div className="flex flex-col space-y-6 pt-6">
                {/* Mobile menu items */}
                <div className="flex flex-col space-y-4">
                  {navbarLinks.map(item => (
                    <Link
                      key={item.name}
                      href={item.href}
                      className="text-base font-medium text-foreground"
                      onClick={() => setOpen(false)}
                    >
                      {item.name}
                    </Link>
                  ))}
                </div>

                {/* Mobile login/signup buttons */}
                <div className="flex flex-col space-y-3 pt-4">
                  {clerkButtons}
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
